import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import {
  CONSENT_ACCEPTED,
  CONSENT_REJECTED,
  getStoredConsent,
  revokeConsent,
  saveConsent,
} from '../../analytics/consent';

export const openAnalyticsConsentPreferences = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('dawntech:open-analytics-preferences'),
    );
  }
};

const AnalyticsConsent = () => {
  const { t } = useTranslation();
  const [choice, setChoice] = React.useState(getStoredConsent);
  const [preferencesOpen, setPreferencesOpen] = React.useState(!choice);

  React.useEffect(() => {
    const handleOpen = () => setPreferencesOpen(true);
    window.addEventListener('dawntech:open-analytics-preferences', handleOpen);
    return () =>
      window.removeEventListener(
        'dawntech:open-analytics-preferences',
        handleOpen,
      );
  }, []);

  const handleChoice = (nextChoice) => {
    saveConsent(nextChoice);
    setChoice(nextChoice);
    setPreferencesOpen(false);
  };

  const handleRevoke = () => {
    revokeConsent();
    setChoice(null);
    setPreferencesOpen(true);
  };

  if (!preferencesOpen) {
    return null;
  }

  return (
    <Paper
      component="aside"
      elevation={8}
      role="dialog"
      aria-label={t('analyticsConsent.title')}
      sx={{
        position: 'fixed',
        bottom: { xs: 8, sm: 24 },
        left: { xs: 8, sm: 'auto' },
        right: { xs: 8, sm: 24 },
        width: { xs: 'auto', sm: 440 },
        maxWidth: 'calc(100vw - 16px)',
        zIndex: 1400,
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('analyticsConsent.title')}
        </Typography>
        <Typography variant="body2">
          {t('analyticsConsent.description')}
        </Typography>
        <Link
          href="/privacy"
          underline="hover"
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('analyticsConsent.privacy')}
        </Link>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => handleChoice(CONSENT_REJECTED)}
          >
            {t('analyticsConsent.reject')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleChoice(CONSENT_ACCEPTED)}
          >
            {t('analyticsConsent.accept')}
          </Button>
          {choice === CONSENT_ACCEPTED && (
            <Button variant="text" onClick={handleRevoke}>
              {t('analyticsConsent.revoke')}
            </Button>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default AnalyticsConsent;
